// 法人情報詳細
import React, { useState, useEffect } from 'react';
import { render } from 'react-dom';
import { Button } from 'react-bootstrap';
import axios from '../../../axios';
import { handleBrowserBack } from '../../../BrowserBack';
import { approval_status } from '../../../Const';
import { UsersLabelType, ResponseDetailType } from '../../../types/Corporate';
import PopupStaff from '../../../molecules/PopupStaff';
import LoadingModal from '../../../molecules/LoadingModal';
import CorporateDataListItems from '../../../organisms/CorporateDataListItems';
import CorporateInfoDetailFooterButtons from '../../../organisms/CorporateInfoDetailFooterButtons';
import { LabelData, UsersLabel } from '../../../const/Corporate';
import { KWMNTGM05030A01 } from '../../../const/api_modules';

const CorporateInfoDetail = () => {
  const [data, setData] = useState<ResponseDetailType | undefined>(undefined);
  // ポップアップの表示制御とメッセージ
  const [is_popup, setIsPopup] = useState(false);
  const [popup_detail, setPopupDetail] = useState({ type: '', message: '' });
  // ローディング画面表示制御
  const [is_loading, setIsLoading] = useState(false);
  // URLパラメータの取得
  const url_params = new URLSearchParams(document.location.search.substring(1));
  const corporate_id = url_params.get('corporate_id');

  useEffect(() => {
    // ブラウザの戻る・進む禁止
    handleBrowserBack();
    // 表示するデータを取得
    getData();
  }, []);

  // 表示するデータを取得
  const getData = async () => {
    try {
      // ローディング画面表示
      setIsLoading(true);

      const detail = [{ id: 0, name: 'id', value: corporate_id }];

      const response = await axios.post(`${location.origin}${process.env.MIX_MOCK_URL}/api/common`, {
        module_name : KWMNTGM05030A01,
        is_json : true,
        reception_id : '0',
        reception_cd : null,
        detail : detail,
      });

      if (response.data.result) {
        // detailの中身をstateにセット
        setData(response.data.detail);
      }
    } catch (error) {
      // console.error(error.response.message);
      // alert(error.response.data.message);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 【戻る】ボタン押下時の処理
   */
  const handleBack = () => {
    location.replace(`${location.origin}${process.env.MIX_MOCK_URL}/staff/corporate`);
  };

  /**
   * 【編集履歴】ボタン押下時の処理
   */
  const handleEditHistory = () => {
    location.replace(
      `${location.origin}${process.env.MIX_MOCK_URL}/staff/corporate_info_edit_history?corporate_id=${corporate_id}`
    );
  };

  /**
   * 【確認】ボタン押下時の処理
   */
  const handleConfirm = () => {
    location.replace(
      `${location.origin}${process.env.MIX_MOCK_URL}/staff/corporate_info_entry_confirm?corporate_id=${corporate_id}`
    );
  };

  /**
   * 【編集】ボタン押下時の処理（法人情報）
   */
  const handleCorporateEdit = () => {
    location.replace(
      `${location.origin}${process.env.MIX_MOCK_URL}/staff/corporate_info_edit?corporate_id=${corporate_id}`
    );
  };

  /**
   * 【編集】ボタン押下時の処理（法人ユーザー情報）
   * @param type 'user1' or 'user2' or 'user3' どの法人ユーザーを編集するかパラメータとして遷移先に渡す
   */
  const handleUserEdit = (type: string) => {
    location.replace(
      `${location.origin}${process.env.MIX_MOCK_URL}/staff/corporate_user_info_edit?corporate_id=${corporate_id}&user_type=${type}`
    );
  };

  /**
   * 【削除】ボタン押下時の処理（法人情報・モーダル表示）
   */
  const handleCorporateDelete = () => {
    setIsPopup(true);
    setPopupDetail({
      type: 'corporate',
      message: 'この法人情報を削除します。よろしいですか？',
    });
  };

  /**
   * 【削除】ボタン押下時の処理（法人ユーザー情報・モーダル表示）
   * @param type 'user1' or 'user2' or 'user3'のどれか
   */
  const handleUserDelete = (type: string) => {
    setIsPopup(true);
    setPopupDetail({
      type: type,
      message: 'この法人ユーザー情報を削除します。よろしいですか？',
    });
  };

  /**
   * 【削除】ボタン押下後、モーダル内で【キャンセル】ボタン押下時の処理
   */
  const handleCancel = () => {
    // モーダルを閉じて、モーダル表示に使用する値をリセット
    setIsPopup(false);
    setPopupDetail({ type: '', message: '' });
  };

  /**
   * 【削除】ボタン押下後、モーダル内で【OK】を押した時の処理
   */
  const handleSubmitDelete = () => {
    // モーダルにtypeを持たせられないため、stateのtypeで判定
    if (popup_detail.type === 'corporate') {
      location.replace(
        `${location.origin}${process.env.MIX_MOCK_URL}/staff/corporate_info_delete?corporate_id=${corporate_id}`
      );
    } else {
      // 法人ユーザー1,2,3の場合は'user1' or 'user2' or 'user3'をパラメータとして遷移先に渡す
      location.replace(
        `${location.origin}${process.env.MIX_MOCK_URL}/staff/corporate_user_info_delete?corporate_id=${corporate_id}&user_type=${popup_detail.type}`
      );
    }
  };

  /**
   * 編集可能ユーザーかどうか判定する処理
   * 以下の条件を全て満たしている場合は編集可能と判断する
   * 1. 承認ステータスが「承認」or「差戻し」の場合は全員が対象、「未承認」の場合はログインユーザーと編集ユーザーが同一の場合のみ
   * 2. 法人情報が論理削除されていない場合
   * @returns boolean
   */
  const isEditableUser = () => {
    const status = data?.corporate_information.approval_status;
    const login_user_id = data?.loginInfo?.user_id;
    const update_user_id = data?.corporate_information.update_user_id;
    return (
        status === approval_status.approved ||
        status === approval_status.send_back ||
        (status === approval_status.unapproved && login_user_id === update_user_id)
      ) && !data?.corporate_information.del_flg
        ? true
        : false;
  };

  /**
   * 【確認】ボタン　承認ステータスが「未承認」かつ、ログインユーザーと編集ユーザーが一致しなければ、承認ユーザーとみなし活性にする処理
   * @returns boolean
   */
  const disabledConfirmBtn = () => {
    const status = data?.corporate_information.approval_status;
    const login_user_id = data?.loginInfo?.user_id;
    const update_user_id = data?.corporate_information.update_user_id;
    return status === approval_status.unapproved && login_user_id !== update_user_id ? false : true;
  };

  /**
   * 法人ユーザー1,2,3枠内の【削除】ボタン　それぞれのデータが登録されているか判定し、登録されていれば活性にする処理
   * @param type 'user1' or 'user2' or 'user3'
   * @returns boolean
   */
  const disabledUserDeleteBtn = (type: string) => {
    const info = data?.corporate_information;
    // 編集ユーザーかどうか判定
    if (isEditableUser()) {
      // typeごとの法人ユーザー1,2,3の名前の登録があるか判定、なければ未登録と判断してtrueを返す
      if (type === 'user1') return !info?.temp_name_1 ? true : false;
      if (type === 'user2') return !info?.temp_name_2 ? true : false;
      if (type === 'user3') return !info?.temp_name_3 ? true : false;
    } else {
      return true;
    }
  };

  /**
   * 【法人情報の削除】ボタン　法人ユーザー1,2,3が登録されているか判定し、3つとも登録されていない場合のみ活性
   * @returns boolean
   */
  const disabledCorporateDeleteBtn = () => {
    const info = data?.corporate_information;
    // 編集ユーザーかどうか判定
    if (isEditableUser()) {
      // どれか1つでも登録されていれば非活性にする
      return info?.temp_name_1 || info?.temp_name_2 || info?.temp_name_3 ? true : false;
    } else {
      return true;
    }
  };

  // レスポンスデータを{key:value}の配列に変換する
  const dataArray = () => {
    return data &&
      Object.entries(data.corporate_information).map(([key, value]) => ({
        key,
        value,
      }));
  };

  return (
    <div className="corporate">
      <div className="title">法人情報</div>
      <div className="container">
        <div className="contents">
          <p className="contents_title mb-5">法人情報詳細</p>
          <div className="corporate_info_wrapper">
            <div className="border_group_wrapper">
              <p className="section_label">法人情報</p>
              <CorporateDataListItems labelData={LabelData.corporate} dataArray={dataArray()} />
              <div className="d-flex justify-content-end">
                <Button
                  className="btn btn-success btn-mini"
                  onClick={handleCorporateEdit}
                  disabled={!isEditableUser()}
                >
                  編集
                </Button>
              </div>
            </div>
            {UsersLabel.map((item: UsersLabelType) => (
              <div className="border_group_wrapper" key={item.type}>
                <p className="section_label">{item.label}</p>
                <CorporateDataListItems labelData={LabelData[item.type]} dataArray={dataArray()} />
                <div className="d-flex justify-content-end">
                  <Button
                    className="btn btn-danger btn-mini mr-3"
                    onClick={() => handleUserDelete(item.type)}
                    disabled={disabledUserDeleteBtn(item.type)}
                  >
                    削除
                  </Button>
                  <Button
                    className="btn btn-success btn-mini"
                    onClick={() => handleUserEdit(item.type)}
                    disabled={!isEditableUser()}
                  >
                    編集
                  </Button>
                </div>
              </div>
            ))}
          </div>
          <CorporateInfoDetailFooterButtons
            handleBack={handleBack}
            handleEditHistory={handleEditHistory}
            handleCorporateDelete={handleCorporateDelete}
            handleConfirm={handleConfirm}
            disabledCorporateDeleteBtn={disabledCorporateDeleteBtn}
            disabledConfirmBtn={disabledConfirmBtn}
          />
        </div>
      </div>
      <PopupStaff
        toggleShow={is_popup}
        text={popup_detail.message}
        btn1_label={'OK'}
        btn1_css={'btn-danger'}
        btn1_func={handleSubmitDelete}
        btn2_label={'キャンセル'}
        btn2_css={'btn-outline-success btn-text-color'}
        btn2_func={handleCancel}
      />
      <LoadingModal show={is_loading} />
    </div>
  );
};

if (document.getElementById('corporate-info-detail')) {
  render(<CorporateInfoDetail />, document.getElementById('corporate-info-detail'));
}
